import { faNoteSticky } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const Shop = ({ user, settings, ws }) => {
  const today = "January 8, 2026";
  const toc = [
    { id: "intro", label: "Privacy Policy" },
    { id: "info-we-collect", label: "Information We Collect" },
    { id: "log-data", label: "Log Data" },
    { id: "device-data", label: "Device Data" },
    { id: "personal-info", label: "Personal Information" },
    { id: "legitimate-reasons", label: "Legitimate Reasons" },
    { id: "collection-use", label: "Collection and Use" },
    { id: "security", label: "Security" },
    { id: "retention", label: "Data Retention" },
    { id: "children", label: "Children’s Privacy" },
    { id: "disclosure", label: "Disclosure to Third Parties" },
    { id: "transfers", label: "International Transfers" },
    { id: "rights", label: "Your Rights & Control" },
    { id: "business-transfers", label: "Business Transfers" },
    { id: "limits", label: "Limits of Our Policy" },
    { id: "changes", label: "Changes to This Policy" },
    { id: "us-states", label: "U.S. States Compliance" },
    { id: "gdpr", label: "GDPR (EU)" },
    { id: "au", label: "Australia (AU)" },
    { id: "pipeda", label: "Canada (PIPEDA)" },
    { id: "uk", label: "UK GDPR" },
    { id: "contact", label: "Contact Us" },
  ];

  return (
    <div className="shop-container">
      <div className="page-wrap">
        <div className="page-header">
          <div className="title">
            <span className="blue">
              <FontAwesomeIcon icon={faNoteSticky} />
            </span>
            <p className="title-text">Privacy Policy</p>
            <div className="title-meta">
              <p className="date">Last updated: {today}</p>
            </div>
          </div>
        </div>

        <div className="policy-layout">
        <aside className="toc">
          <div className="toc-inner">
            <p className="toc-title">On this page</p>
            <nav>
              {toc.map((t) => (
                <a key={t.id} href={`#${t.id}`} className="toc-link">
                  {t.label}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        <main className="content">
          <section id="intro" className="section">
            <p>
              Your privacy is important to us. It is GrindBux's policy to respect your privacy and comply with any applicable law and regulation regarding any personal information we may collect about you, including across our website, <a href="https://grindbux.com" target="_blank" rel="noopener noreferrer">grindbux.com</a>, and other sites we own and operate.
            </p>
            <p>
              Personal information is any information about you which can be used to identify you. This includes information about you as a person (such as name, address, and date of birth), your devices, payment details, and even information about how you use a website or online service.
            </p>
            <p>
              In the event our site contains links to third-party sites and services, please be aware that those sites and services have their own privacy policies. After following a link to any third-party content, you should read their posted privacy policy information about how they collect and use personal information. This Privacy Policy does not apply to any of your activities after you leave our site.
            </p>
          </section>

          <section className="section">
            <p className="muted">This policy is effective as of {today}</p>
            <p className="muted">Last updated: {today}</p>
          </section>

          <section id="info-we-collect" className="section">
            <h2 className="section-title">Information We Collect</h2>
            <p>
              Information we collect falls into one of two categories: "voluntarily provided" information and "automatically collected" information. "Voluntarily provided" information refers to any information you knowingly and actively provide us when using or participating in any of our services and promotions. "Automatically collected" information refers to any information automatically sent by your devices in the course of accessing our products and services.
            </p>
          </section>

          <section id="log-data" className="section">
            <h3 className="sub-title">Log Data</h3>
            <p>
              When you visit our website, our servers may automatically log the standard data provided by your web browser. It may include your device’s Internet Protocol (IP) address, your browser type and version, the pages you visit, the time and date of your visit, the time spent on each page, and other details about your visit.
            </p>
            <p>
              Additionally, if you encounter certain errors while using the site, we may automatically collect data about the error and the circumstances surrounding its occurrence. This data may include technical details about your device, what you were trying to do when the error happened, and other technical information relating to the problem. You may or may not receive notice of such errors, even in the moment they occur, that they have occurred, or what the nature of the error is.
            </p>
            <p>
              Please be aware that while this information may not be personally identifying by itself, it may be possible to combine it with other data to personally identify individual persons.
            </p>
          </section>

          <section id="device-data" className="section">
            <h3 className="sub-title">Device Data</h3>
            <p>
              When you visit our website or interact with our services, we may automatically collect data about your device, such as: Device Type, Operating system, Unique device identifiers.
            </p>
            <p>
              Data we collect can depend on the individual settings of your device and software. We recommend checking the policies of your device manufacturer or software provider to learn what information they make available to us.
            </p>
          </section>

          <section id="personal-info" className="section">
            <h3 className="sub-title">Personal Information</h3>
            <p>
              We may ask for personal information — for example, when you submit content to us, when you subscribe to our newsletter, when you register an account or when you contact us — which may include one or more of the following: Name, Email, Social media profiles, Date of birth, Phone/mobile number.
            </p>
          </section>

          <section id="legitimate-reasons" className="section">
            <h2 className="section-title">Legitimate Reasons for Processing Your Personal Information</h2>
            <p>
              We only collect and use your personal information when we have a legitimate reason for doing so. In which instance, we only collect personal information that is reasonably necessary to provide our services to you.
            </p>
          </section>

          <section id="collection-use" className="section">
            <h2 className="section-title">Collection and Use of Information</h2>
            <p>
              We may collect personal information from you when you do any of the following on our website: Register for an account; Sign up to receive updates from us via email or social media channels; Use a mobile device or web browser to access our content; Contact us via email, social media, or on any similar technologies; When you mention us on social media.
            </p>
            <p>
              We may collect, hold, use, and disclose information for the following purposes, and personal information will not be further processed in a manner that is incompatible with these purposes: to provide you with our platform's core features and services; to enable you to customise or personalise your experience of our website; for analytics, market research, and business development; to send you marketing and promotional communications (you may opt out); to deliver personalized and targeted advertising; for internal record keeping and administrative purposes; for security and fraud prevention.
            </p>
            <p>
              We may combine voluntarily provided and automatically collected personal information with general information or research data we receive from other trusted sources.
            </p>
          </section>

          <section id="security" className="section">
            <h2 className="section-title">Security of Your Personal Information</h2>
            <p>
              When we collect and process personal information, and while we retain this information, we will protect it within commercially acceptable means to prevent loss and theft, as well as unauthorised access, disclosure, copying, use or modification. Although we will do our best to protect the personal information you provide to us, we advise that no method of electronic transmission or storage is 100% secure and no one can guarantee absolute data security.
            </p>
            <p>
              You are responsible for selecting any password and its overall security strength, ensuring the security of your own information within the bounds of our services.
            </p>
          </section>

          <section id="retention" className="section">
            <h2 className="section-title">How Long We Keep Your Personal Information</h2>
            <p>
              We keep your personal information only for as long as we need to for the purposes set out in this privacy policy or as required by law. When no longer required, we will delete it or anonymise it.
            </p>
          </section>

          <section id="children" className="section">
            <h2 className="section-title">Children’s Privacy</h2>
            <p>
              We do not aim any of our products or services directly at children under the age of 13 and we do not knowingly collect personal information about children under 13.
            </p>
          </section>

          <section id="disclosure" className="section">
            <h2 className="section-title">Disclosure of Personal Information to Third Parties</h2>
            <p>
              We may disclose personal information to: a parent, subsidiary or affiliate of our company; third-party service providers; our employees, contractors, and/or related entities; our existing or potential agents or business partners; credit reporting agencies, courts, tribunals, and regulatory authorities; courts, tribunals, regulatory authorities, and law enforcement officers; third parties, including agents or sub-contractors; third parties to collect and process data; an entity that buys, or to which we transfer all or substantially all of our assets and business.
            </p>
            <p>
              Third parties we currently use include: Google Analytics.
            </p>
          </section>

          <section id="transfers" className="section">
            <h2 className="section-title">International Transfers of Personal Information</h2>
            <p>
              The personal information we collect is stored and/or processed in Australia, United States of America (the) or where we or our partners, affiliates, and third-party providers maintain facilities. Transfers will be performed in accordance with applicable law and protected in accordance with this privacy policy.
            </p>
          </section>

          <section id="rights" className="section">
            <h2 className="section-title">Your Rights and Controlling Your Personal Information</h2>
            <p>
              Your choice: By providing personal information to us, you understand we will collect, hold, use, and disclose your personal information in accordance with this privacy policy. You do not have to provide personal information to us; however, if you do not, it may affect your use of our website or the products and/or services offered on or through it.
            </p>
            <p>
              Information from third parties: If we receive personal information about you from a third party, we will protect it as set out in this privacy policy. If you are a third party providing personal information about somebody else, you represent and warrant that you have such person’s consent to provide the personal information to us.
            </p>
            <p>
              Marketing permission: If you have previously agreed to us using your personal information for direct marketing purposes, you may change your mind at any time by contacting us using the details below.
            </p>
            <p>
              Access: You may request details of the personal information that we hold about you.
            </p>
            <p>
              Correction: If you believe that any information we hold about you is inaccurate, out of date, incomplete, irrelevant, or misleading, please contact us using the details provided in this privacy policy. We will take reasonable steps to correct any information found to be inaccurate, incomplete, misleading, or out of date.
            </p>
            <p>
              Non-discrimination: We will not discriminate against you for exercising any of your rights over your personal information. Unless your personal information is required to provide you with a particular service or offer, we will not deny you goods or services or charge you different prices or rates, or provide you with a different level or quality of goods or services.
            </p>
            <p>
              Downloading of personal information: We provide a means for you to download the personal information you have shared through our site. Please contact us for more information.
            </p>
            <p>
              Notification of data breaches: We will comply with laws applicable to us in respect of any data breach.
            </p>
            <p>
              Complaints: If you believe that we have breached a relevant data protection law and wish to make a complaint, please contact us using the details below and provide us with full details of the alleged breach. We will promptly investigate your complaint and respond to you, in writing, setting out the outcome of our investigation and the steps we will take to deal with your complaint. You also have the right to contact a regulatory body or data protection authority in relation to your complaint.
            </p>
            <p>
              Opt out of communications: You may opt out of receiving communications from us, including marketing and promotional messages, at any time. Use the opt-out facilities provided in the communication (for example, click the “unsubscribe” link in emails, reply “STOP” or “UNSUBSCRIBE” to SMS), or contact us directly. In certain cases, we may request additional information to verify your identity before processing your opt-out request.
            </p>
            <p>
              Even if you opt out of communications, we may still contact you when necessary for non-promotional purposes, including, but not limited to, managing your account, responding to service inquiries, or providing important updates related to your use of our services.
            </p>
          </section>

          <section id="business-transfers" className="section">
            <h2 className="section-title">Business Transfers</h2>
            <p>
              If we or our assets are acquired, or in the unlikely event that we go out of business or enter bankruptcy, we would include data, including your personal information, among the assets transferred to any parties who acquire us, subject to applicable law and this policy.
            </p>
          </section>

          <section id="limits" className="section">
            <h2 className="section-title">Limits of Our Policy</h2>
            <p>
              Our website may link to external sites that are not operated by us. We have no control over the content and policies of those sites.
            </p>
          </section>

          <section id="changes" className="section">
            <h2 className="section-title">Changes to This Policy</h2>
            <p>
              We may change our privacy policy to reflect updates to our business processes, acceptable practices, or legislative/regulatory changes. Significant changes will be communicated as required.
            </p>
          </section>

          <section id="us-states" className="section">
            <h2 className="section-title">Additional Disclosures for U.S. States Privacy Law Compliance</h2>
            <h3 className="sub-title">Do Not Track</h3>
            <p>
              Some browsers have a "Do Not Track" feature that lets you tell websites that you do not want to have your online activities tracked. At this time, we do not respond to browser "Do Not Track" signals. We adhere to the standards outlined in this privacy policy.
            </p>
            <h3 className="sub-title">California Privacy Laws - CPPA</h3>
            <p>
              Under California Civil Code Section 1798.83, if you live in California and your business relationship with us is mainly for personal, family, or household purposes, you may ask us about the information we release to other organizations for their marketing purposes.
            </p>
            <p>
              In accordance with your right to non-discrimination, we may offer you certain financial incentives permitted by the California Consumer Privacy Act, and the California Privacy Rights Act (collectively, CCPA) that can result in different prices, rates, or quality levels for the goods or services we provide. Any CCPA-permitted financial incentive we offer will reasonably relate to the value of your personal information, and we will provide written terms that describe clearly the nature of such an offer. Participation in a financial incentive program requires your prior opt-in consent, which you may revoke at any time.
            </p>
            <p>
              To make a request, contact us using the details in this privacy policy with “Request for California privacy information” in the subject line. You may make this type of request once every calendar year. We will email you a list of categories of personal information we revealed to other organisations for their marketing purposes in the last calendar year, along with their names and addresses.
            </p>
            <h3 className="sub-title">California Notice of Collection</h3>
            <p>
              In the past 12 months, we have collected the following categories of personal information enumerated in the CCPA: identifiers (such as name, email address, phone number, account name, IP address, and an ID or number assigned to your account), and geolocation data. See “Information We Collect”.
            </p>
            <h3 className="sub-title">Right to Know and Delete</h3>
            <p>
              You have rights to delete your personal information we collected and know certain information about our data practices in the preceding 12 months. In particular, you have the right to request the following from us: (i) The categories of personal information we have collected about you; (ii) The categories of sources from which the personal information was collected; (iii) The categories of personal information about you we disclosed for a business purpose or sold; (iv) The categories of third parties to whom the personal information was disclosed for a business purpose or sold; (v) The business or commercial purpose for collecting or selling the personal information; and (vi) The specific pieces of personal information we have collected about you.
            </p>
            <h3 className="sub-title">Shine the Light</h3>
            <p>
              You have the right to request information from us regarding the manner in which we share certain personal information with third parties and affiliates for their own direct marketing purposes. Send requests with “Privacy Rights Request” and include your name, street address, city, state, and ZIP code.
            </p>
          </section>

          <section id="gdpr" className="section">
            <h2 className="section-title">Additional Disclosures for General Data Protection Regulation (GDPR) Compliance (EU)</h2>
            <h3 className="sub-title">Data Controller / Data Processor</h3>
            <p>
              We, GrindBux, are a Data Controller and/or Processor with respect to the personal information you provide to us.
            </p>
            <h3 className="sub-title">Legal Bases for Processing</h3>
            <p>
              We will only collect and use your personal information when we have a legal right to do so, lawfully, fairly, and transparently. If we seek your consent to process your personal information, and you are under 16 years of age, we will seek your parent or legal guardian’s consent. Our lawful bases include: Consent; Performance of a Contract or Transaction; Our Legitimate Interests (e.g., research and development, understanding our audience, marketing and promoting our services, operating efficiently, protecting legal rights); and Compliance with the Law.
            </p>
            <h3 className="sub-title">International Transfers Outside of the EEA</h3>
            <p>
              Transfers will be protected by appropriate safeguards such as standard contractual clauses or binding corporate rules.
            </p>
            <h3 className="sub-title">Your Rights and Control</h3>
            <p>
              Restrict: You have the right to request that we restrict the processing of your personal information if (i) you are concerned about the accuracy of your personal information; (ii) you believe your personal information has been unlawfully processed; (iii) you need us to maintain the personal information solely for the purpose of a legal claim; or (iv) we are in the process of considering your objection in relation to processing on the basis of legitimate interests.
            </p>
            <p>
              Object: You have the right to object to processing of your personal information that is based on our legitimate interests or public interest. If this is done, we must provide compelling legitimate grounds to proceed.
            </p>
            <p>
              Data portability: You may have the right to request a copy of the personal information we hold about you, in CSV or other machine-readable format, and to request transfer to a third party.
            </p>
            <p>
              Deletion: You may have a right to request that we delete the personal information we hold about you. If you terminate or delete your account, we will delete your personal information within 30 days of the deletion of your account, subject to legal exceptions.
            </p>
          </section>

          <section id="au" className="section">
            <h2 className="section-title">Additional Disclosures for Australian Privacy Act Compliance (AU)</h2>
            <p>
              Some third parties may not be regulated by the Privacy Act and Australian Privacy Principles; such parties may not be accountable under the Privacy Act.
            </p>
          </section>

          <section id="pipeda" className="section">
            <h2 className="section-title">Additional Disclosures for PIPEDA Compliance (Canada)</h2>
            <p>
              Additional Scope of Personal Information: We broaden our definition of personal information to include any information about an individual, such as financial information, appearance, views and opinions, opinions held about you by others, and any personal correspondences you may have with us.
            </p>
            <p>
              Valid Consent: Consent is only valid if it is reasonable to expect that an individual would understand the nature, purpose, and consequences of the collection, use, or disclosure. When you contact us, we assume consent based on your positive action of contact.
            </p>
            <p>
              International Transfers of Information: While GrindBux endeavors to keep, store and handle customer data within Canada, it may use agents or service providers located in the U.S., EEA or UK.
            </p>
            <p>
              Customer Data Rights: PIPEDA grants consumers the right to access, correct inaccurate or outdated personal information (or delete if not possible), and withdraw consent for activities (e.g., direct marketing or cookies).
            </p>
            <p>
              Right of Access: Make your access request in writing and pay a minimal fee of $30.00. We will take necessary measures to fulfill your request in 30 days from receipt, or inform you of inability to do so before 30 days in specified circumstances. We may extend the time limit to convert personal information into an alternative format, notifying you of the delay within the first 30 days.
            </p>
            <p>
              Right of Rectification: You may request a correction to factual errors or omissions within your PII. If successfully demonstrated incomplete or inaccurate, we must amend accordingly.
            </p>
            <p>
              Compliance with PIPEDA's Ten Principles of Privacy:
            </p>
            <ul>
              <li>1. Accountability</li>
              <li>2. Identifying purposes</li>
              <li>3. Consent</li>
              <li>4. Limiting collection</li>
              <li>5. Limiting use, disclosure and retention</li>
              <li>6. Accuracy</li>
              <li>7. Safeguards</li>
              <li>8. Openness</li>
              <li>9. Customer access</li>
              <li>10. Challenging compliance</li>
            </ul>
            <p>
              Anti-Spam Legislation: We do not send unsolicited email to persons with whom we have no relationship and will not sell personal information to unrelated third-parties.
            </p>
            <p>
              Enquiries, Reports and Escalation: If we fail to resolve your concern, you may contact the Office of the Privacy Commissioner of Canada: 30 Victoria Street, Gatineau, QC K1A 1H3. Toll Free: 1.800.282.1376. www.priv.gc.ca
            </p>
          </section>

          <section id="uk" className="section">
            <h2 className="section-title">Additional Disclosures for UK GDPR Compliance (UK)</h2>
            <p>
              Data Controller / Data Processor: We, GrindBux, are a Data Controller and/or Processor with respect to the personal information you provide to us.
            </p>
            <p>
              Third-Party Provided Content: We may indirectly collect personal information from third-parties who have your permission to share it, and publicly available information depending on your privacy settings.
            </p>
            <p>
              Additional Disclosure for Collection and Use: We may conduct marketing and market research activities, including how visitors use our site and user experience.
            </p>
            <p>
              Legal Bases for Processing: We will collect and use your personal information lawfully, fairly and transparently. We never directly market to any person(s) under 18 years of age.
            </p>
            <p>
              International Transfers of Personal Information: The personal information we collect is stored and/or processed in the United Kingdom by us. Where we share your data with third parties based outside of the UK or EEA, we will adopt appropriate safeguards such as SCCs or binding corporate rules.
            </p>
            <p>
              Your Data Subject Rights: Right to Restrict Processing; Right to Object; Right to be Informed; Right of Access (DSAR within 30 days); Right to Erasure (qualified right); Right to Portability (technically feasible, electronic data given to GrindBux); Right to Rectification.
            </p>
            <p>
              Notification of data breaches: Upon discovery of a data breach, we will investigate and report it to the UK's data protection regulator and to you, if appropriate.
            </p>
            <p>
              Complaints: You may lodge a complaint with the ICO. We appreciate the chance to deal with your concerns first. ICO: Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF. Tel: 0303 123 1113. Website: www.ico.org.uk
            </p>
          </section>

          <section id="contact" className="section">
            <h2 className="section-title">Contact Us</h2>
            <p>
              For any questions or concerns regarding your privacy, you may contact us using the following details:
            </p>
            <p>
              GrindBux support team — <a href="mailto:support@grindbux.com">support@grindbux.com</a>
            </p>
          </section>
        </main>
      </div>
      </div>

      <style jsx>{`
        :global(html) { scroll-behavior: smooth; }

        .shop-container {
          width: 100%;
          height: 100%;
          padding: 32px 16px;
          overflow-x: hidden;
          overflow-y: auto;
          background: #171D39; /* site background */
        }

        .shop-container::-webkit-scrollbar { display: none; }

        .page-wrap {
          max-width: 1200px;
          margin: 0 auto;
        }

        .page-header {
          background: #141A31; /* match content container */
          border: 1px solid #1c2344;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 24px;
        }

        .title {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .title-text {
          font-weight: 600;
          font-size: 24px;
          margin: 0;
          color: #ffffff;
        }

        .title-meta {
          margin-left: auto;
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .date { margin: 0; font-size: 14px; color: #9fbad6; }
        .blue { color: #4DAEFF; }

        .policy-layout {
          display: grid;
          grid-template-columns: 260px 1fr;
          gap: 24px;
        }

        .toc { position: sticky; top: 24px; height: fit-content; }

        .toc-inner {
          background: #1c2344; /* site card bg */
          border: 1px solid #262E58;
          border-radius: 10px;
          padding: 16px;
        }

        .toc-title {
          margin: 0 0 8px 0;
          font-size: 13px;
          color: #9fbad6;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .toc-link {
          display: block;
          color: #ffffff;
          text-decoration: none;
          font-size: 14px;
          padding: 6px 0;
        }

        .toc-link:hover { color: #4F8DDE; }

        .content {
          background: #141A31; /* site content bg */
          border: 1px solid #1c2344;
          border-radius: 12px;
          padding: 24px;
        }

        .section { margin: 0 0 24px 0; }

        .section-title {
          margin: 0 0 8px 0;
          font-size: 20px;
          font-weight: 600;
          color: #ffffff;
        }

        .sub-title {
          margin: 16px 0 6px 0;
          font-size: 16px;
          font-weight: 600;
          color: #d7e7f7;
        }

        p { color: #ffffff; line-height: 1.6; overflow-wrap: anywhere; }
        .muted { color: #9fbad6; }
        a { color: #4DAEFF; }

        @media (max-width: 900px) {
          .policy-layout {
            grid-template-columns: 1fr;
          }
          .toc { position: relative; top: auto; }
          .toc-inner { margin-bottom: 16px; }
          .title-meta { width: 100%; justify-content: flex-start; }
        }
      `}</style>
    </div>
  );
};

export default Shop;
